import axios from 'axios'
import { AuditLedger, GrievanceRecord, GrievanceStatus } from '../../domain/entities/Grievance'
import {
  GetGrievancesFilters,
  GetGrievancesResponse,
  GrievanceRepository,
} from '../../domain/repositories/GrievanceRepository'
import { GrievanceApi, grievanceApiDatasource } from '../datasources/GrievanceApiDatasource'

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined
    return data?.message || data?.error || fallback
  }

  return err instanceof Error ? err.message : fallback
}

const toGrievanceRecord = (g: GrievanceApi): GrievanceRecord => ({
  id: g.id,
  orderId: g.order_id,
  raisedBy: g.raised_by,
  buyerName: g.buyer_name,
  buyerType: g.buyer_type,
  status: g.status as GrievanceStatus,
  reason: g.reason,
  assignedAmId: g.assigned_am_id,
  assignedAmName: g.assigned_am_name,
  description: g.description,
  evidenceUrls: g.evidence_urls,
  notes: g.resolution_notes || g.description,
  resolvedAt: g.resolved_at,
  resolvedBy: g.resolved_by,
  resolutionNotes: g.resolution_notes,
  createdAt: g.created_at,
  updatedAt: g.updated_at,
  orderedUnits: g.ordered_units || 0,
  receivedUnits: g.received_units || 0,
  affectedUnits: g.affected_units || 0,
  issue: g.description || g.reason || 'Unknown issue',
})

export class GrievanceRepositoryImpl implements GrievanceRepository {
  public async getGrievances(filters: GetGrievancesFilters): Promise<GetGrievancesResponse> {
    try {
      const res = await grievanceApiDatasource.getGrievances({
        skip: (filters.page - 1) * filters.limit,
        limit: filters.limit,
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.search ? { search: filters.search } : {}),
      })

      return {
        records: res.data.map(toGrievanceRecord),
        totalItems: res.total,
        totalPages: Math.max(1, Math.ceil(res.total / filters.limit)),
      }
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load grievances.'))
    }
  }

  public async getGrievanceById(id: string): Promise<GrievanceRecord | null> {
    try {
      const res = await grievanceApiDatasource.getGrievanceById(id)
      return toGrievanceRecord(res)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null
      throw new Error(extractErrorMessage(err, 'Failed to load grievance.'))
    }
  }

  public async getAuditLedger(id: string): Promise<AuditLedger | null> {
    try {
      const res = await grievanceApiDatasource.getAuditLedger(id)
      return {
        grievanceId: res.grievance_id,
        orderId: res.order_id,
        totalFarmersCount: res.total_farmers_count,
        dispatchedQty: res.factory_to_fpo.dispatched_qty,
        gateReceivedQty: res.factory_to_fpo.gate_received_qty,
        shortageDeficit: res.factory_to_fpo.shortage_deficit,
        affectedUnits: res.factory_to_fpo.affected_units,
        reportedIssue: res.factory_to_fpo.reported_issue,
        fpoToFarmers: (res.fpo_to_farmers || []).map((f) => ({
          farmerName: f.farmer_name,
          phoneNumber: f.phone_number,
          units: f.units,
          pricePerUnit: f.price_per_unit,
          totalValue: f.total_value,
        })),
        fpoToVlcc: (res.fpo_to_vlcc || []).map((v) => ({
          vlccName: v.vlcc_name,
          unitsReceived: v.units_received,
          vlccToFarmers: (v.vlcc_to_farmers || []).map((f) => ({
            farmerName: f.farmer_name,
            phoneNumber: f.phone_number,
            units: f.units,
            pricePerUnit: f.price_per_unit,
            totalValue: f.total_value,
          })),
        })),
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null
      // Return null on ledger endpoint failure so page falls back gracefully
      return null
    }
  }

  public async assignAm(id: string, amId: string, _amName: string): Promise<GrievanceRecord> {
    try {
      const res = await grievanceApiDatasource.assignAm(id, { assigned_am_id: amId })
      return toGrievanceRecord(res)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to assign AM.'))
    }
  }

  public async resolveGrievance(id: string, notes: string): Promise<GrievanceRecord> {
    try {
      const res = await grievanceApiDatasource.resolveGrievance(id, { resolution_notes: notes })
      return toGrievanceRecord(res)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to resolve grievance.'))
    }
  }
}

export const grievanceRepository = new GrievanceRepositoryImpl()
